/* import __COLOCATED_TEMPLATE__ from './fin-phone-csat-rating.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { tracked } from '@glimmer/tracking';
import {
  FIN_VOICE_CSAT_TTS_OPTIONS,
  type FinVoiceCsatTtsOptions,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-csat-tts-options';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    phoneNumberId?: string;
    numberConfiguration?: FinVoiceNumberConfiguration;
    updateAskForCsatRating: (
      askForCsatRating: boolean,
      csatMessage: string,
      csatTtsLanguage: string,
    ) => Promise<void>;
  };
}

export default class FinPhoneCsatRating extends Component<Signature> {
  @service declare intl: IntlService;
  @tracked askForCsatRating =
    this.args.numberConfiguration?.handoverConfiguration?.askForCsatRating ?? false;
  @tracked csatMessage = this.args.numberConfiguration?.csatMessage ?? '';
  @tracked csatTtsLanguage =
    this.args.numberConfiguration?.handoverConfiguration?.csatTtsLanguage ?? 'en-US';

  get csatTtsOptions() {
    let options: FinVoiceCsatTtsOptions[] = [];

    FIN_VOICE_CSAT_TTS_OPTIONS.forEach((option) => {
      options.push({
        ...option,
        text: this.intl.t(option.text),
      });
    });

    return options;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get hasChanges() {
    return (
      this.askForCsatRating !== this.numberConfiguration?.handoverConfiguration?.askForCsatRating ||
      this.csatMessage !== this.numberConfiguration?.csatMessage ||
      this.csatTtsLanguage !== this.numberConfiguration?.handoverConfiguration?.csatTtsLanguage
    );
  }

  get savingDisabled() {
    return !this.hasChanges;
  }

  get askForCsatRatingLabel() {
    return this.askForCsatRating
      ? this.intl.t('operator.fin.setup.tabs.fin-voice-csat-rating.enabled')
      : this.intl.t('operator.fin.setup.tabs.fin-voice-csat-rating.not-enabled');
  }

  @action
  toggleAskForCsatRating() {
    this.askForCsatRating = !this.askForCsatRating;
  }

  @action
  resetAskForCsatRating() {
    this.askForCsatRating =
      this.numberConfiguration?.handoverConfiguration?.askForCsatRating ?? false;
    this.csatMessage = this.numberConfiguration?.csatMessage ?? '';
    this.csatTtsLanguage =
      this.numberConfiguration?.handoverConfiguration?.csatTtsLanguage ?? 'en-US';
  }

  @action
  updateCsatMessage(event: Event) {
    let target = event.target as HTMLTextAreaElement;
    this.csatMessage = target.value;
  }

  @action
  updateCsatTtsLanguage(language: string) {
    this.csatTtsLanguage = language;
  }

  @action
  async updateAskForCsatRating() {
    await this.args.updateAskForCsatRating(
      this.askForCsatRating,
      this.csatMessage,
      this.csatTtsLanguage,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::FinPhoneCsatRating': typeof FinPhoneCsatRating;
  }
}
